import { isArray, round } from 'lodash'
import { DEFAULT_STYLING } from 'app/views/icons/defaults'

const MAX_SWATCH_COLORS = 8

const DEFAULT_SWATCH_PRIMARY_COLOR = 'rgba(255, 255, 255, 1)'
const DEFAULT_SWATCH_SECONDARY_COLOR = 'rgba(255, 255, 255, 0.4)'

const DEFAULT_PREVIEW_MODE = 'day'
const PREVIEW_MODE_KEY = 'previewMode'

export function getPreviewMode() {
  const previewMode = localStorage.getItem(PREVIEW_MODE_KEY)
  if (previewMode) {
    return previewMode
  } else {
    resetPreviewMode()
    return DEFAULT_PREVIEW_MODE
  }
}

export function updatePreviewMode(mode) {
  localStorage.setItem(PREVIEW_MODE_KEY, mode)
}

export function resetPreviewMode() {
  localStorage.setItem(PREVIEW_MODE_KEY, DEFAULT_PREVIEW_MODE)
}

// Helper function used for setting style['--fa-primary-color'] and style['--fa-secondary-color'] on Icon List containers
export function resolveColor(color, defaultColor) {
  if (color === null) {
    // No explicit color provided; fall back to inherited/default styling
    return 'inherit'
  }
  return color !== defaultColor ? removeOpacity(color) : 'inherit'
}

export function getColorSync() {
  const currentValue = localStorage.getItem('colorSync')
  const colorSync = currentValue === null ? true : currentValue === 'true'
  return colorSync
}

export function enableColorSync() {
  localStorage.setItem('colorSync', 'true')
  return true
}

export function disableColorSync() {
  localStorage.setItem('colorSync', 'false')
  return false
}

function swatchStorageName(isPrimary) {
  return isPrimary ? 'primarySwatches' : 'secondarySwatches'
}

export function resetColorsToDefault() {
  addColorSwatch(DEFAULT_STYLING.colorPrimary, true)
  addColorSwatch(DEFAULT_STYLING.colorSecondary, false)
}

export function getColorSwatches(isPrimary) {
  let swatches = JSON.parse(localStorage.getItem(swatchStorageName(isPrimary)))

  if (isArray(swatches)) {
    return swatches
  } else {
    // not found - init swatches
    const defaults = defaultSwatches(isPrimary)
    localStorage.setItem(swatchStorageName(isPrimary), JSON.stringify(defaults))
    return defaults
  }
}

export function getColorSwatchesHexString(isPrimary) {
  const swatches = getColorSwatches(isPrimary)
  return swatches.join('; ')
}

export function getLastColor(isPrimary) {
  const swatches = getColorSwatches(isPrimary)

  if (swatches.length > 0) {
    return swatches[0]
  } else {
    return isPrimary ? DEFAULT_STYLING.colorPrimary : DEFAULT_STYLING.colorSecondary
  }
}

export function addColorSwatch(color, isPrimary) {
  let swatches = getColorSwatches(isPrimary)

  // Check to see if new color is the same as current color
  if (swatches[0]?.toString() !== color.toString()) {
    if (swatches.length == MAX_SWATCH_COLORS) {
      // We are at the max number of colors, remove item from the end of the array
      swatches.pop()
    }

    // Add new item to the start of the array
    swatches.unshift(color)
    localStorage.setItem(swatchStorageName(isPrimary), JSON.stringify(swatches))
  }

  return swatches
}

function defaultSwatches(isPrimary) {
  const swatches = isPrimary
    ? Array(MAX_SWATCH_COLORS - 1).fill(DEFAULT_SWATCH_PRIMARY_COLOR)
    : Array(MAX_SWATCH_COLORS - 1).fill(DEFAULT_SWATCH_SECONDARY_COLOR)

  swatches.unshift(isPrimary ? DEFAULT_STYLING.colorPrimary : DEFAULT_STYLING.colorSecondary)
  return swatches
}

export function getOpacity(color) {
  switch (getColorFormat(color)) {
    case 'hexa':
      return getOpacityFromHexa(color)
    default:
      return getAlpha(color)
  }
}

function getColorFormat(color) {
  return color.startsWith('#') ? 'hexa' : color.slice(0, 4)
}

export function updateOpacity(color, newOpacity) {
  if (!color) return color

  const format = getColorFormat(color)

  switch (format) {
    case 'hexa': {
      // Convert opacity (0-1) to hex (00-FF)
      const alphaHex = Math.round(newOpacity * 255)
        .toString(16)
        .padStart(2, '0')
        .toUpperCase()
      const hex = color.replace('#', '')

      if (hex.length === 8) {
        // Full format with alpha: #RRGGBBAA
        return '#' + hex.slice(0, 6) + alphaHex
      } else if (hex.length === 6) {
        // No alpha, add it: #RRGGBB -> #RRGGBBAA
        return '#' + hex + alphaHex
      } else if (hex.length === 4) {
        // Shorthand with alpha: #RGBA
        const shortAlpha = alphaHex[0]
        return '#' + hex.slice(0, 3) + shortAlpha
      } else if (hex.length === 3) {
        // Shorthand without alpha: #RGB -> #RGBA
        const shortAlpha = alphaHex[0]
        return '#' + hex + shortAlpha
      }
      return color
    }

    case 'rgba': {
      // rgba(r, g, b, a) - replace alpha value
      const values = color
        .substring(5, color.length - 1)
        .split(',')
        .map((v) => v.trim())
      values[3] = newOpacity.toString()
      return `rgba(${values.join(', ')})`
    }

    case 'rgb(': {
      // rgb(r, g, b) - convert to rgba
      const values = color
        .substring(4, color.length - 1)
        .split(',')
        .map((v) => v.trim())
      return `rgba(${values.join(', ')}, ${newOpacity})`
    }

    case 'hsla': {
      // hsla(h, s, l, a) - replace alpha value
      const values = color
        .substring(5, color.length - 1)
        .split(',')
        .map((v) => v.trim())
      values[3] = newOpacity.toString()
      return `hsla(${values.join(', ')})`
    }

    case 'hsva': {
      // hsva(h, s, v, a) - replace alpha value
      const values = color
        .substring(5, color.length - 1)
        .split(',')
        .map((v) => v.trim())
      values[3] = newOpacity.toString()
      return `hsva(${values.join(', ')})`
    }

    default:
      return color
  }
}

export function getOpacityFromHexa(hexa) {
  if (!hexa) return 1.0

  // Remove # if present
  const hex = hexa.replace('#', '')

  // Handle shorthand notation (#RGBA -> #RRGGBBAA)
  if (hex.length === 4) {
    const alpha = hex[3]
    const alphaValue = parseInt(alpha + alpha, 16)
    return round(alphaValue / 255, 2)
  }

  // Handle full notation (#RRGGBBAA)
  if (hex.length === 8) {
    const alpha = hex.substring(6, 8)
    const alphaValue = parseInt(alpha, 16)
    return round(alphaValue / 255, 2)
  }

  // No alpha channel present (#RGB or #RRGGBB)
  return 1.0
}

export function getAlpha(colorString) {
  if (!colorString || !colorString.endsWith(')')) {
    console.error('Invalid color string format: ', colorString)
    return null
  }

  // Check if it's rgba, hsla, or hsva format
  let startIndex = -1
  if (colorString.startsWith('rgba(')) {
    startIndex = 5
  } else if (colorString.startsWith('rgb(')) {
    startIndex = 4
  } else if (colorString.startsWith('hsla(')) {
    startIndex = 5
  } else if (colorString.startsWith('hsva(')) {
    startIndex = 5
  } else {
    console.error('Invalid color format. Must be rgba, hsla, or hsva. Received value:', colorString)
    return null
  }

  // Extract the part inside the parentheses
  const values = colorString.substring(startIndex, colorString.length - 1).split(',')

  // The fourth value is the alpha channel
  const alpha = parseFloat(values[3])

  // The alpha value is a number between 0.0 (fully transparent) and 1.0 (fully opaque)
  return isNaN(alpha) ? null : alpha
}

/**
 * Removes opacity/alpha channel from a color string of any format
 * @param {string} color - Color string in any format (hex, rgba, rgb, hsla, hsva, hsl, hsv)
 * @returns {string} Color string without opacity/alpha channel
 */
export function removeOpacity(color) {
  if (!color) return color

  const format = getColorFormat(color)

  switch (format) {
    case 'hexa': {
      // Handle hex colors with alpha
      const hex = color.replace('#', '')

      // Handle 8-character hex (RRGGBBAA) -> RRGGBB
      if (hex.length === 8) {
        return '#' + hex.slice(0, 6)
      }
      // Handle 4-character hex (RGBA) -> RGB
      if (hex.length === 4) {
        return '#' + hex.slice(0, 3)
      }
      // Already without alpha (RGB or RRGGBB)
      return color
    }

    case 'rgba': {
      // rgba(r, g, b, a) -> rgb(r, g, b)
      const values = color
        .substring(5, color.length - 1)
        .split(',')
        .map((v) => v.trim())
      return `rgb(${values[0]}, ${values[1]}, ${values[2]})`
    }

    case 'rgb(': {
      // Already no alpha
      return color
    }

    case 'hsla': {
      // hsla(h, s, l, a) -> hsl(h, s, l)
      const values = color
        .substring(5, color.length - 1)
        .split(',')
        .map((v) => v.trim())
      return `hsl(${values[0]}, ${values[1]}, ${values[2]})`
    }

    case 'hsl(': {
      // Already no alpha
      return color
    }

    case 'hsva': {
      // hsva(h, s, v, a) -> hsv(h, s, v)
      const values = color
        .substring(5, color.length - 1)
        .split(',')
        .map((v) => v.trim())
      return `hsv(${values[0]}, ${values[1]}, ${values[2]})`
    }

    case 'hsv(': {
      // Already no alpha
      return color
    }

    default:
      // Unknown format, return as-is
      return color
  }
}
